import type { ReactNode } from "react";
import type { Fiat } from "@/api/client";
import { cn } from "@/lib/utils";

// ISO 3166-1 alpha-2 (or "eu") country codes for the flag-icons CSS classes
// (`fi fi-<code>`). Emoji flags were tried first but Windows/Chrome renders
// regional-indicator pairs as plain letters instead of flags — flag-icons
// ships actual SVGs so it renders consistently everywhere.
// Keep in sync with `Fiat` in api/client/types.ts.
const CURRENCY_COUNTRY: Record<Fiat, string> = {
  EUR: "eu",
  USD: "us",
  GBP: "gb",
  SEK: "se",
  CHF: "ch",
  HKD: "hk",
  CAD: "ca",
  JPY: "jp",
  DKK: "dk",
  CNY: "cn",
  AUD: "au",
  PLN: "pl",
  CZK: "cz",
  NOK: "no",
  TWD: "tw",
  KZT: "kz",
  KRW: "kr",
};

interface CurrencyFlagProps {
  code: string | null | undefined;
  /** Square flag, for icon tiles. Its size follows the font size (1em). */
  square?: boolean;
  className?: string;
  /** Shown instead when the currency has no flag. */
  fallback?: ReactNode;
}

export const CurrencyFlag = ({
  code,
  square = false,
  className,
  fallback = null,
}: CurrencyFlagProps) => {
  const country = code ? CURRENCY_COUNTRY[code as Fiat] : undefined;
  if (!country) return <>{fallback}</>;
  return (
    <span
      className={cn(
        "fi",
        `fi-${country}`,
        square ? "fis" : "rounded-[2px] align-middle",
        className,
      )}
      aria-hidden="true"
    />
  );
};
